import React, { useEffect } from 'react';
import Navbar from '../../components/Navbar';

function usePageMeta(title, description) {
  useEffect(() => {
    const prevTitle = document.title;
    const meta = document.querySelector('meta[name="description"]');
    const prevDesc = meta ? meta.getAttribute('content') : '';
    let createdMeta = null;

    document.title = title;
    if (meta) {
      meta.setAttribute('content', description);
    } else {
      createdMeta = document.createElement('meta');
      createdMeta.name = 'description';
      createdMeta.content = description;
      document.head.appendChild(createdMeta);
    }

    return () => {
      document.title = prevTitle;
      if (createdMeta) {
        createdMeta.remove();
      } else if (meta) {
        meta.setAttribute('content', prevDesc || '');
      }
    };
  }, [title, description]);
}

export default function HealthSafetyInspection() {
  usePageMeta(
    'Health and Safety Inspection Software for Teams | BuildMyChecks',
    'Standardise site safety inspections, toolbox talks, and RAMS verification with digital health and safety inspection software built for businesses. Capture photo evidence, timestamped records, and audit-ready trails.'
  );

  const schema = {
    '@context': 'https://schema.org',
    '@type': 'SoftwareApplication',
    name: 'BuildMyChecks',
    applicationCategory: 'BusinessApplication',
    applicationSubCategory: 'HealthAndSafetyManagement',
    operatingSystem: 'Web, iOS, Android',
    description:
      'BuildMyChecks is a digital health and safety inspection software for businesses. Standardise inspections, capture photo evidence, and maintain clear audit trails for compliance.',
    url: 'https://buildmychecks.com/health-and-safety-inspection-software',
    offers: {
      '@type': 'Offer',
      price: '5.00',
      priceCurrency: 'GBP',
      availability: 'https://schema.org/InStock',
    },
    publisher: {
      '@type': 'Organization',
      name: 'BuildMyChecks',
      url: 'https://buildmychecks.com',
    },
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-emerald-50 to-gray-200">
      <Navbar />

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }}
      />

      <main className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <header className="text-center mb-12">
          <p className="text-sm font-semibold text-primary uppercase tracking-wide mb-2">Health & Safety</p>
          <h1 className="text-4xl sm:text-5xl font-extrabold text-gray-900 mb-4">Health and Safety Inspection Software for Teams</h1>
          <p className="text-lg text-gray-700 max-w-3xl mx-auto">
            Standardise site safety inspections, toolbox talks, and RAMS verification with digital health and safety inspection software built for businesses. Capture photo evidence, timestamped records, and clear audit trails to keep teams safe, compliant, and inspection-ready.
          </p>
        </header>

        <section className="bg-white rounded-2xl shadow-xl p-8 mb-10">
          <h2 className="text-2xl font-bold text-gray-900 mb-3">Why teams choose BuildMyChecks for health and safety inspections</h2>
          <p className="text-gray-700 mb-4">BuildMyChecks helps teams move away from paper and spreadsheets with a simple, mobile-first way to manage health and safety checks across sites and teams.</p>
          <ul className="space-y-3 text-gray-800 list-disc list-inside">
            <li>Assign health and safety inspections with clear due dates and ownership</li>
            <li>Capture defects and hazards using annotated photos and notes</li>
            <li>Automatically generate PDF inspection reports for HSE readiness</li>
            <li>Control access with role-based permissions for supervisors and inspectors</li>
            <li>Complete inspections offline, with automatic sync when back online</li>
          </ul>
        </section>

        <section className="bg-white rounded-2xl shadow-xl p-8 mb-10">
          <h2 className="text-2xl font-bold text-gray-900 mb-3">Standardise every health and safety inspection</h2>
          <p className="text-gray-700 mb-4">Use reusable templates for:</p>
          <ul className="space-y-2 text-gray-800 list-disc list-inside">
            <li>Site safety inspections</li>
            <li>Fire safety checks</li>
            <li>Equipment and machinery inspections</li>
            <li>Toolbox talks and RAMS verification</li>
          </ul>
          <p className="text-gray-700 mt-4">Standardised digital checklists ensure every inspection asks the right questions and captures consistent evidence across all locations.</p>
        </section>

        <section className="bg-white rounded-2xl shadow-xl p-8 mb-10">
          <h2 className="text-2xl font-bold text-gray-900 mb-3">Stay audit-ready without chasing paperwork</h2>
          <ul className="space-y-2 text-gray-800 list-disc list-inside mb-4">
            <li>Every inspection is time-stamped and linked to the inspector</li>
            <li>Stored securely with photo evidence and notes</li>
            <li>Digital signatures, corrective actions, and clear audit trails</li>
          </ul>
          <p className="text-gray-700">Easily demonstrate compliance during internal reviews or external audits. For broader audit coverage, see our <a className="text-primary font-semibold hover:text-red-700" href="/compliance-checklist-software-uk">compliance checklist software</a>.</p>
        </section>

        <section className="bg-gradient-to-br from-gray-50 via-emerald-50 to-white border border-emerald-100 rounded-2xl shadow-xl p-8 mb-10">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">How BuildMyChecks works</h2>
          <ol className="list-decimal list-inside space-y-3 text-gray-800">
            <li>Create or import your health and safety inspection checklists.</li>
            <li>Assign inspections to teams with due dates and reminders.</li>
            <li>Inspectors capture issues with photos, notes, and signatures.</li>
            <li>Supervisors review and approve, then export PDF reports instantly.</li>
          </ol>
          <p className="text-gray-700 mt-4">No spreadsheets. No lost paperwork. No uncertainty.</p>
          <div className="flex flex-wrap gap-3 mt-6">
            <a href="/register" className="inline-flex items-center px-4 py-3 bg-primary text-white rounded-lg font-semibold shadow hover:bg-red-700">Get audit-ready inspections</a>
            <a href="/features" className="inline-flex items-center px-4 py-3 bg-white text-primary border border-primary rounded-lg font-semibold shadow hover:bg-gray-50">See how safety inspections work</a>
          </div>
        </section>

        <section className="bg-white rounded-2xl shadow-xl p-8 mb-10">
          <h2 className="text-2xl font-bold text-gray-900 mb-3">Built for real-world site conditions</h2>
          <ul className="space-y-2 text-gray-800 list-disc list-inside">
            <li>Mobile-first inspections on phones and tablets</li>
            <li>Offline support for remote or low-signal locations</li>
            <li>Fast reporting without admin overhead</li>
          </ul>
        </section>

        <section className="bg-white rounded-2xl shadow-xl p-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Ready to modernise health and safety inspections?</h2>
          <p className="text-gray-700 mb-4">Move away from paper forms and Excel files. Give your teams a reliable, mobile way to complete consistent health and safety checks — and keep all inspection evidence in one place.</p>
          <div className="flex flex-wrap gap-3">
            <a href="/register" className="inline-flex items-center px-5 py-3 bg-primary text-white rounded-lg font-semibold shadow hover:bg-red-700">Get audit-ready inspections</a>
            <a href="/features" className="inline-flex items-center px-5 py-3 bg-white text-primary border border-primary rounded-lg font-semibold shadow hover:bg-gray-50">See how safety inspections work</a>
          </div>
        </section>
      </main>
    </div>
  );
}
